import { OTP_CONFIG } from '@porterin/booking-flow/src/auth/domain/config/otp';
import Input from '@porterin/component-library/src/base-components/input/Input';
import React, { forwardRef, Fragment, useRef, useState } from 'react';

type OtpInputInteractions = {
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
};

type OtpInputOptions = {
  disabled?: boolean;
  error?: string;
  length?: number;
};

type OtpIdentifiers = {
  id: string;
};

interface OtpInputProps {
  identifiers: OtpIdentifiers;
  interactions?: OtpInputInteractions;
  inputOptions?: OtpInputOptions;
  className?: string;
}

const OtpInput = forwardRef<HTMLDivElement, OtpInputProps>(
  (
    {
      identifiers,
      interactions,
      inputOptions = { length: OTP_CONFIG.MAX_CHAR_LENGTH },
      className = '',
    },
    ref,
  ) => {
    const [otp, setOtp] = useState<string[]>(
      Array(inputOptions.length ?? OTP_CONFIG.MAX_CHAR_LENGTH).fill(''),
    );
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handleChange = (index: number, value: string) => {
      if (!OTP_CONFIG.NUMERIC_REGEX.test(value)) return;

      const newOtp = [...otp];
      newOtp[index] = value.slice(-1);
      setOtp(newOtp);

      interactions?.onChange?.(newOtp.join(''));

      if (
        value &&
        index < (inputOptions.length || OTP_CONFIG.MAX_CHAR_LENGTH) - 1
      ) {
        inputRefs.current[index + 1]?.focus();
      }

      if (newOtp.every(val => val !== '')) {
        interactions?.onComplete?.(newOtp.join(''));
      }
    };

    const handleKeyDown = (
      index: number,
      e: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (e.key === 'Backspace' && !otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');
      if (!OTP_CONFIG.NUMERIC_REGEX.test(pastedData)) return;

      const newOtp = [...otp];
      const maxOtpLength = inputOptions.length ?? OTP_CONFIG.MAX_CHAR_LENGTH;
      const pastedChars = pastedData.slice(0, maxOtpLength).split('');

      pastedChars.forEach((char, index) => {
        newOtp[index] = char;
      });

      setOtp(newOtp);
      interactions?.onChange?.(newOtp.join(''));

      if (newOtp.every(val => val !== '')) {
        interactions?.onComplete?.(newOtp.join(''));
      }
    };

    return (
      <div
        ref={ref}
        className={`flex flex-col gap-2 ${className}`}
        id={identifiers?.id}
      >
        <div className="flex flex-row gap-3">
          {Array.from({
            length: inputOptions.length ?? OTP_CONFIG.MAX_CHAR_LENGTH,
          }).map((_, index) => (
            <Fragment key={`${identifiers.id}-${index}`}>
              <Input
                ref={el => {
                  inputRefs.current[index] = el;
                }}
                identifiers={{ id: `${identifiers.id}-${index}` }}
                inputOptions={{
                  ...inputOptions,
                  placeholder: '',
                }}
                autoFocus={index === 0}
                value={otp[index]}
                onChange={e => handleChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={`
                w-full
                text-center text-[2rem] font-bold
                rounded-lg
                ${inputOptions?.error ? 'border-[#DB230A] focus:border-[#DB230A]' : 'border-gray-300'}
              `}
                maxLength={1}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </Fragment>
          ))}
        </div>
        {inputOptions?.error && (
          <div
            id={`otp-input-error-${identifiers.id}`}
            className="mt-3 text-left text-lg font-normal text-red-500"
          >
            {inputOptions.error}
          </div>
        )}
      </div>
    );
  },
);

OtpInput.displayName = 'OtpInput';
export default OtpInput;
